import { useEffect, useMemo, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import MoreVertRounded from "@mui/icons-material/MoreVertRounded";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Portal from "@mui/material/Portal";
import Skeleton from "@mui/material/Skeleton";
import Snackbar from "@mui/material/Snackbar";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  useProgramPreviewIdentity,
  useProgramPreviewPage,
  useProgramPreviewQuery,
} from "@bw/shared/hooks";
import { useForm } from "react-hook-form";
import { z } from "zod";

import {
  createSessionOverride,
  deleteSessionOverride,
  listSessionPreview,
  listSessions,
  type ProgramTrackOptionResponse,
  type UnifiedProgramResponse,
  type UnifiedProgramSessionResponse,
} from "@/libs/programsApi";
import { AttendanceDialog } from "./program-attendance-dialog";
import { TeachingEvidenceDialog } from "./program-teaching-evidence-dialog";
import {
  datetimeLocalToUtc,
  dayKey,
  formatDayLabel,
  formatTimeRange,
  initialVisibleMonth,
  isRenderableSession,
  monthLabel,
  monthWindow,
  stepMonth,
  type VisibleMonth,
} from "./program-detail-time";

export type CourtOption = { id: string; name: string };

type Feedback = {
  message: string;
  severity: "error" | "success";
};

type CancelValues = { reason: string };
type ReassignValues = { courtIds: string[] };
type RescheduleValues = { newEnd: string; newStart: string };
type OverridePayload = Parameters<typeof createSessionOverride>[1];

const cancelSchema = z.object({ reason: z.string() });
const reassignSchema = z.object({ courtIds: z.array(z.string()) });

function isCourtOnlyReschedule(
  session: UnifiedProgramSessionResponse,
): boolean {
  if (session.override_action !== "reschedule") return false;
  if (!session.occurrence_start || !session.occurrence_end) return false;
  const start = new Date(session.start_time).getTime();
  const base = new Date(session.occurrence_start).getTime();
  const end = new Date(session.end_time).getTime();
  const baseEnd = new Date(session.occurrence_end).getTime();
  return (
    !Number.isNaN(start) &&
    !Number.isNaN(base) &&
    start === base &&
    !Number.isNaN(end) &&
    !Number.isNaN(baseEnd) &&
    end === baseEnd
  );
}

function overrideBadge(
  session: UnifiedProgramSessionResponse,
): { color: "error" | "info"; label: string } | undefined {
  if (session.override_action === "cancel") {
    return { color: "error", label: "Cancelled" };
  }
  if (session.override_action === "reschedule") {
    return isCourtOnlyReschedule(session)
      ? { color: "info", label: "Courts changed" }
      : { color: "info", label: "Rescheduled" };
  }
  return undefined;
}

function isPastSession(session: UnifiedProgramSessionResponse): boolean {
  const end = new Date(session.end_time).getTime();
  return !Number.isNaN(end) && end <= Date.now();
}

function baseOccurrenceIso(session: UnifiedProgramSessionResponse): string {
  return session.occurrence_start ?? session.start_time;
}

export function SessionsSection({
  program,
  tracks,
  courts,
  optionsReady,
}: {
  program: UnifiedProgramResponse;
  tracks: readonly ProgramTrackOptionResponse[];
  courts: CourtOption[];
  optionsReady: boolean;
}) {
  const queryClient = useQueryClient();
  const identity = useProgramPreviewIdentity();
  const timeZone = program.timezone || "UTC";
  const [visibleMonth, setVisibleMonth] = useState<VisibleMonth>(() =>
    initialVisibleMonth(program.start_date, program.timezone),
  );
  const [cancelTarget, setCancelTarget] =
    useState<UnifiedProgramSessionResponse | null>(null);
  const [rescheduleTarget, setRescheduleTarget] =
    useState<UnifiedProgramSessionResponse | null>(null);
  const [removeTarget, setRemoveTarget] =
    useState<UnifiedProgramSessionResponse | null>(null);
  const [reassignTarget, setReassignTarget] =
    useState<UnifiedProgramSessionResponse | null>(null);
  const [reassignError, setReassignError] = useState<string | null>(null);
  const [attendanceTarget, setAttendanceTarget] =
    useState<UnifiedProgramSessionResponse | null>(null);
  const [attendanceOpen, setAttendanceOpen] = useState(false);
  const [teachingTarget, setTeachingTarget] =
    useState<UnifiedProgramSessionResponse | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuSession, setMenuSession] =
    useState<UnifiedProgramSessionResponse | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const overrideInFlight = useRef(false);
  const [selectedTrackId, setSelectedTrackId] = useState<string>("");

  const { windowStart, windowEnd } = useMemo(
    () => monthWindow(visibleMonth, timeZone),
    [visibleMonth, timeZone],
  );
  const privateProgram = program.audience === "private_coaching";
  const activeTrackIds = tracks
    .filter((track) => track.status === "active")
    .map((track) => track.track_id);
  useEffect(() => {
    if (
      optionsReady &&
      selectedTrackId &&
      !tracks.some((track) => track.track_id === selectedTrackId)
    )
      setSelectedTrackId("");
  }, [tracks, selectedTrackId, optionsReady]);
  const scope = [
    identity,
    "business-programs",
    program.program_id,
    program.facility_id,
    "authenticated",
    "sessions",
    windowStart,
    windowEnd,
    selectedTrackId || null,
  ];
  const sessionsQuery = useProgramPreviewPage({
    scope,
    enabled: !privateProgram,
    load: (cursor, signal) =>
      listSessionPreview(
        program.program_id,
        {
          window_start: windowStart,
          window_end: windowEnd,
          limit: 50,
          ...(selectedTrackId ? { track_id: selectedTrackId } : {}),
          ...(cursor ? { cursor } : {}),
        },
        signal,
      ),
  });
  const privateScope = [
    identity,
    "business-programs",
    program.program_id,
    program.facility_id,
    "authenticated",
    "private-exact-sessions",
    windowStart,
    windowEnd,
    tracks[0]?.track_id,
  ];
  const privateQuery = useProgramPreviewQuery({
    scope: privateScope,
    enabled: privateProgram && tracks.length === 1,
    load: () =>
      listSessions(
        program.program_id,
        windowStart,
        windowEnd,
        tracks[0]?.track_id,
      ),
  });
  const activeQuery = privateProgram ? privateQuery : sessionsQuery;
  const sessionsReady = activeQuery.ready;
  const sessions = privateProgram
    ? (privateQuery.data ?? [])
    : (sessionsQuery.data?.items.map((item) => item.session) ?? []);
  function sessionsAreCurrent(): boolean {
    if (!sessionsReady) return false;
    const current = queryClient
      .getQueryCache()
      .findAll({
        queryKey: [
          "program-preview",
          ...(privateProgram ? privateScope : scope),
        ],
      })
      .find((entry) => entry.state.data === activeQuery.data)?.state;
    return (
      current?.status === "success" &&
      current.fetchStatus === "idle" &&
      !current.isInvalidated &&
      current.data === activeQuery.data
    );
  }
  function sessionIsCurrent(session: UnifiedProgramSessionResponse): boolean {
    return sessionsAreCurrent() && sessions.includes(session);
  }
  function trackIsCurrentlyActive(
    session: UnifiedProgramSessionResponse,
  ): boolean {
    if (!session.track_id || !sessionIsCurrent(session)) return false;
    if (
      !privateProgram &&
      !selectedTrackId &&
      sessionsQuery.data?.examined_track_id === session.track_id
    )
      return true;
    return activeTrackIds.includes(session.track_id);
  }
  useEffect(() => {
    if (overrideInFlight.current) return;
    const targets = [
      menuSession,
      cancelTarget,
      rescheduleTarget,
      removeTarget,
      reassignTarget,
      attendanceTarget,
      teachingTarget,
    ];
    if (
      sessionsAreCurrent() &&
      targets.every((target) => target === null || sessions.includes(target))
    )
      return;
    setMenuAnchor(null);
    setMenuSession(null);
    setCancelTarget(null);
    setRescheduleTarget(null);
    setRemoveTarget(null);
    setReassignTarget(null);
    setAttendanceTarget(null);
    setAttendanceOpen(false);
    setTeachingTarget(null);
  }, [
    sessionsReady,
    activeQuery.data,
    menuSession,
    cancelTarget,
    rescheduleTarget,
    removeTarget,
    reassignTarget,
    attendanceTarget,
    teachingTarget,
  ]);

  const rescheduleSchema = useMemo(
    () =>
      z
        .object({
          newStart: z
            .string()
            .min(1, "Enter both a new start and a new end time."),
          newEnd: z
            .string()
            .min(1, "Enter both a new start and a new end time."),
        })
        .superRefine((values, context) => {
          const start = datetimeLocalToUtc(values.newStart, timeZone);
          const end = datetimeLocalToUtc(values.newEnd, timeZone);
          if (!start || !end) return;
          if (end.getTime() <= start.getTime()) {
            context.addIssue({
              code: "custom",
              message: "The new end must be after the new start.",
              path: ["newEnd"],
            });
          }
        }),
    [timeZone],
  );

  const cancelForm = useForm<CancelValues>({
    defaultValues: { reason: "" },
    resolver: zodResolver(cancelSchema),
  });
  const rescheduleForm = useForm<RescheduleValues>({
    defaultValues: { newEnd: "", newStart: "" },
    resolver: zodResolver(rescheduleSchema),
  });
  const reassignForm = useForm<ReassignValues>({
    defaultValues: { courtIds: [] },
    resolver: zodResolver(reassignSchema),
  });
  const reassignCourtIds = reassignForm.watch("courtIds");

  const createOverrideMutation = useMutation({
    mutationFn: (payload: OverridePayload) => {
      if (!sessionsAreCurrent())
        throw new Error("Session information changed. Refresh before editing.");
      return createSessionOverride(program.program_id, payload);
    },
  });
  const deleteOverrideMutation = useMutation({
    mutationFn: (overrideId: string) => {
      if (!sessionsAreCurrent())
        throw new Error("Session information changed. Refresh before editing.");
      return deleteSessionOverride(overrideId);
    },
  });

  const trackName = (trackId: string | null | undefined): string | null => {
    if (!trackId) return null;
    return (
      sessionsQuery.data?.items.find(
        (item) => item.session.track_id === trackId,
      )?.track_name ??
      tracks.find((track) => track.track_id === trackId)?.name ??
      null
    );
  };

  const courtNames = (courtIds: string[] | null | undefined): string => {
    if (!courtIds || courtIds.length === 0) return "—";
    const names = courtIds
      .map((id) => courts.find((court) => court.id === id)?.name)
      .filter((name): name is string => Boolean(name));
    return names.length > 0 ? names.join(", ") : "—";
  };

  const refreshSessions = async () => {
    await activeQuery.refetch({ throwOnError: true });
  };

  const openMenu = (
    event: React.MouseEvent<HTMLElement>,
    session: UnifiedProgramSessionResponse,
  ) => {
    if (!sessionIsCurrent(session)) return;
    setMenuAnchor(event.currentTarget);
    setMenuSession(session);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuSession(null);
  };

  const openCancel = (session: UnifiedProgramSessionResponse) => {
    if (!sessionIsCurrent(session)) return;
    closeMenu();
    cancelForm.reset({ reason: "" });
    setCancelTarget(session);
  };
  const closeCancel = () => {
    if (overrideInFlight.current) return;
    setCancelTarget(null);
    cancelForm.reset({ reason: "" });
  };
  const confirmCancel = cancelForm.handleSubmit(async ({ reason }) => {
    if (
      overrideInFlight.current ||
      !cancelTarget ||
      !sessionIsCurrent(cancelTarget)
    )
      return;
    overrideInFlight.current = true;
    try {
      await createOverrideMutation.mutateAsync({
        track_id: cancelTarget.track_id ?? undefined,
        occurrence_start: baseOccurrenceIso(cancelTarget),
        action: "cancel",
        reason: reason.trim() || undefined,
      });
      await refreshSessions();
      setFeedback({ severity: "success", message: "Session cancelled" });
      setCancelTarget(null);
      cancelForm.reset({ reason: "" });
    } catch (error) {
      setFeedback({
        severity: "error",
        message:
          error instanceof Error
            ? error.message
            : "Failed to cancel this session.",
      });
      setCancelTarget(null);
      cancelForm.reset({ reason: "" });
    } finally {
      overrideInFlight.current = false;
    }
  });

  const openReschedule = (session: UnifiedProgramSessionResponse) => {
    if (!sessionIsCurrent(session)) return;
    closeMenu();
    rescheduleForm.reset({ newEnd: "", newStart: "" });
    setRescheduleTarget(session);
  };
  const closeReschedule = () => {
    if (overrideInFlight.current) return;
    setRescheduleTarget(null);
    rescheduleForm.reset({ newEnd: "", newStart: "" });
  };
  const confirmReschedule = rescheduleForm.handleSubmit(
    async ({ newEnd, newStart }) => {
      if (
        overrideInFlight.current ||
        !rescheduleTarget ||
        !sessionIsCurrent(rescheduleTarget)
      )
        return;
      const startUtc = datetimeLocalToUtc(newStart, timeZone);
      const endUtc = datetimeLocalToUtc(newEnd, timeZone);
      if (!startUtc || !endUtc) return;

      overrideInFlight.current = true;
      try {
        await createOverrideMutation.mutateAsync({
          track_id: rescheduleTarget.track_id ?? undefined,
          occurrence_start: baseOccurrenceIso(rescheduleTarget),
          action: "reschedule",
          new_start: startUtc.toISOString(),
          new_end: endUtc.toISOString(),
          ...(rescheduleTarget.is_override &&
          rescheduleTarget.track_id &&
          rescheduleTarget.override_action === "reschedule" &&
          Array.isArray(rescheduleTarget.court_ids)
            ? { court_ids: rescheduleTarget.court_ids }
            : {}),
        });
        await refreshSessions();
        setFeedback({ severity: "success", message: "Session rescheduled" });
        setRescheduleTarget(null);
        rescheduleForm.reset({ newEnd: "", newStart: "" });
      } catch (error) {
        setFeedback({
          severity: "error",
          message:
            error instanceof Error
              ? error.message
              : "Failed to reschedule this session.",
        });
        setRescheduleTarget(null);
        rescheduleForm.reset({ newEnd: "", newStart: "" });
      } finally {
        overrideInFlight.current = false;
      }
    },
  );

  const openRemove = (session: UnifiedProgramSessionResponse) => {
    if (!sessionIsCurrent(session)) return;
    closeMenu();
    setRemoveTarget(session);
  };
  const closeRemove = () => {
    if (overrideInFlight.current) return;
    setRemoveTarget(null);
  };
  const confirmRemove = async () => {
    if (
      overrideInFlight.current ||
      !removeTarget ||
      !sessionIsCurrent(removeTarget)
    )
      return;
    overrideInFlight.current = true;
    try {
      const overrideId = removeTarget.applied_override_id;
      if (!overrideId) {
        setFeedback({
          severity: "error",
          message:
            "Couldn't find the override to remove. Please refresh and try again.",
        });
        setRemoveTarget(null);
        return;
      }
      await deleteOverrideMutation.mutateAsync(overrideId);
      await refreshSessions();
      setFeedback({ severity: "success", message: "Override removed" });
      setRemoveTarget(null);
    } catch (error) {
      setFeedback({
        severity: "error",
        message:
          error instanceof Error
            ? error.message
            : "Failed to remove this override.",
      });
      setRemoveTarget(null);
    } finally {
      overrideInFlight.current = false;
    }
  };

  const openReassign = (session: UnifiedProgramSessionResponse) => {
    if (!sessionIsCurrent(session)) return;
    closeMenu();
    reassignForm.reset({
      courtIds: (session.court_ids ?? []).filter((id) =>
        courts.some((court) => court.id === id),
      ),
    });
    setReassignError(null);
    setReassignTarget(session);
  };
  const closeReassign = () => {
    if (overrideInFlight.current) return;
    setReassignTarget(null);
    reassignForm.reset({ courtIds: [] });
    setReassignError(null);
  };
  const confirmReassign = reassignForm.handleSubmit(async ({ courtIds }) => {
    if (
      overrideInFlight.current ||
      !reassignTarget ||
      !sessionIsCurrent(reassignTarget)
    )
      return;
    overrideInFlight.current = true;
    setReassignError(null);
    try {
      await createOverrideMutation.mutateAsync({
        track_id: reassignTarget.track_id ?? undefined,
        occurrence_start: baseOccurrenceIso(reassignTarget),
        action: "reschedule",
        new_start: reassignTarget.start_time,
        new_end: reassignTarget.end_time,
        court_ids: courtIds,
      });
      await refreshSessions();
      setFeedback({ severity: "success", message: "Courts reassigned" });
      setReassignTarget(null);
      reassignForm.reset({ courtIds: [] });
    } catch (error) {
      setReassignError(
        error instanceof Error
          ? error.message
          : "Couldn't update this session's court reservation.",
      );
    } finally {
      overrideInFlight.current = false;
    }
  });

  const { groups, hiddenCount } = useMemo(() => {
    const renderable = sessions.filter(isRenderableSession);
    const ordered = renderable
      .slice()
      .sort((left, right) => left.start_time.localeCompare(right.start_time));
    const byDay = new Map<
      string,
      { label: string; items: UnifiedProgramSessionResponse[] }
    >();
    for (const session of ordered) {
      const key = dayKey(session.start_time, timeZone);
      const existing = byDay.get(key);
      if (existing) {
        existing.items.push(session);
      } else {
        byDay.set(key, {
          label: formatDayLabel(session.start_time, timeZone),
          items: [session],
        });
      }
    }
    return {
      groups: Array.from(byDay.values()),
      hiddenCount: sessions.length - renderable.length,
    };
  }, [sessions, timeZone]);

  return (
    <Paper
      aria-labelledby="program-sessions-heading"
      component="section"
      sx={{ p: { xs: 2, sm: 3 } }}
      variant="outlined"
    >
      <Stack spacing={2}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
              justifyContent: "space-between",
              flexGrow: 1,
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <Typography
              component="h2"
              id="program-sessions-heading"
              variant="h6"
            >
              Sessions
            </Typography>
            {!privateProgram ? (
              <Button
                size="small"
                aria-label="Refresh sessions"
                startIcon={<RefreshRounded />}
                sx={{ minHeight: 44 }}
                disabled={overrideInFlight.current || sessionsQuery.isLoading}
                onClick={sessionsQuery.restart}
              >
                Refresh
              </Button>
            ) : null}
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Tooltip title="Previous month">
              <IconButton
                aria-label="Previous month"
                onClick={() => setVisibleMonth((month) => stepMonth(month, -1))}
              >
                <ChevronLeftRounded />
              </IconButton>
            </Tooltip>
            <Typography
              sx={{ minWidth: 128, textAlign: "center" }}
              variant="body2"
            >
              {monthLabel(visibleMonth)}
            </Typography>
            <Tooltip title="Next month">
              <IconButton
                aria-label="Next month"
                onClick={() => setVisibleMonth((month) => stepMonth(month, 1))}
              >
                <ChevronRightRounded />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {!privateProgram ? (
          <FormControl size="small">
            <InputLabel id="preview-option-label" shrink>
              Option
            </InputLabel>
            <Select
              labelId="preview-option-label"
              label="Option"
              value={selectedTrackId}
              displayEmpty
              disabled={overrideInFlight.current}
              onChange={(event) => setSelectedTrackId(event.target.value)}
            >
              <MenuItem value="">All options</MenuItem>
              {tracks.map((track) => (
                <MenuItem key={track.track_id} value={track.track_id}>
                  {track.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}
        {!privateProgram && sessionsQuery.data?.examined_track_name ? (
          <Typography>{sessionsQuery.data.examined_track_name}</Typography>
        ) : null}
        {activeQuery.isError ? (
          <Alert severity="error">
            We couldn&apos;t load sessions for this month. Please try again.
          </Alert>
        ) : !sessionsReady ? (
          <Stack spacing={1}>
            <Typography role="status">
              {activeQuery.fetchStatus === "paused"
                ? "Waiting for a connection to refresh sessions…"
                : "Loading sessions…"}
            </Typography>
            <Skeleton height={28} variant="rounded" width="30%" />
            <Skeleton height={52} variant="rounded" />
            <Skeleton height={52} variant="rounded" width="75%" />
          </Stack>
        ) : groups.length === 0 ? (
          <Stack spacing={1} sx={{ py: 1 }}>
            <Typography color="text.secondary">
              {!privateProgram && sessionsQuery.canNext
                ? "More options remain"
                : `No sessions on this page for ${monthLabel(visibleMonth)}.`}
            </Typography>
            {hiddenCount > 0 ? (
              <Typography color="text.secondary" variant="caption">
                {hiddenCount} session{hiddenCount === 1 ? "" : "s"} hidden —
                incomplete schedule data.
              </Typography>
            ) : null}
          </Stack>
        ) : (
          <Stack spacing={2}>
            <List disablePadding>
              {groups.map((group, groupIndex) => (
                <Box
                  component="li"
                  key={group.label}
                  sx={{ listStyle: "none" }}
                >
                  {groupIndex > 0 ? <Divider sx={{ my: 2 }} /> : null}
                  <Typography
                    color="text.secondary"
                    sx={{ mb: 1 }}
                    variant="overline"
                  >
                    {group.label}
                  </Typography>
                  <List disablePadding>
                    {group.items.map((session, index) => {
                      const override = overrideBadge(session);
                      const name = trackName(session.track_id);
                      const cancelled = session.override_action === "cancel";
                      const timeLabel = formatTimeRange(
                        session.start_time,
                        session.end_time,
                        timeZone,
                      );
                      return (
                        <ListItem
                          disableGutters
                          divider={index < group.items.length - 1}
                          key={`${session.program_id}:${session.track_id ?? "program"}:${baseOccurrenceIso(session)}`}
                          sx={{ py: 1.5 }}
                        >
                          <Stack
                            direction={{ xs: "column", md: "row" }}
                            spacing={1.5}
                            sx={{
                              alignItems: { xs: "stretch", md: "center" },
                              width: "100%",
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              sx={{ alignItems: "center", flexWrap: "wrap" }}
                            >
                              <Typography
                                sx={
                                  cancelled
                                    ? {
                                        color: "text.secondary",
                                        textDecoration: "line-through",
                                      }
                                    : undefined
                                }
                                variant="body2"
                              >
                                {timeLabel}
                              </Typography>
                              {name ? (
                                <Typography
                                  color="text.secondary"
                                  variant="body2"
                                >
                                  {name}
                                </Typography>
                              ) : null}
                              {override ? (
                                <Chip
                                  color={override.color}
                                  label={override.label}
                                  size="small"
                                  variant="outlined"
                                />
                              ) : null}
                            </Stack>
                            <Typography
                              color="text.secondary"
                              sx={{ flex: 1 }}
                              variant="caption"
                            >
                              Courts: {courtNames(session.court_ids)}
                            </Typography>
                            <Stack
                              direction="row"
                              spacing={1}
                              sx={{
                                alignItems: "center",
                                alignSelf: "flex-end",
                              }}
                            >
                              {session.track_id && !cancelled ? (
                                <Button
                                  aria-label={`Take attendance for the ${timeLabel} session`}
                                  onClick={() => {
                                    if (!sessionIsCurrent(session)) return;
                                    setAttendanceTarget(session);
                                    setAttendanceOpen(true);
                                  }}
                                  size="small"
                                  variant="outlined"
                                >
                                  Take attendance
                                </Button>
                              ) : null}
                              {session.track_id && isPastSession(session) ? (
                                <Button
                                  aria-label={`Review teaching for the ${timeLabel} session`}
                                  onClick={() => {
                                    if (sessionIsCurrent(session))
                                      setTeachingTarget(session);
                                  }}
                                  size="small"
                                  variant="outlined"
                                >
                                  Review teaching
                                </Button>
                              ) : null}
                              <Tooltip title="Session actions">
                                <IconButton
                                  aria-label={`Session actions for the ${timeLabel} session`}
                                  onClick={(event) => openMenu(event, session)}
                                  size="small"
                                >
                                  <MoreVertRounded />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </Stack>
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              ))}
            </List>
            {hiddenCount > 0 ? (
              <Typography color="text.secondary" variant="caption">
                {hiddenCount} session{hiddenCount === 1 ? "" : "s"} hidden —
                incomplete schedule data.
              </Typography>
            ) : null}
          </Stack>
        )}
        {!privateProgram &&
        (sessionsQuery.canPrevious || sessionsQuery.canNext) ? (
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{
              flexWrap: "wrap",
              "& .MuiButton-root": { whiteSpace: "nowrap" },
            }}
          >
            <Button
              size="small"
              disabled={
                overrideInFlight.current ||
                !sessionsReady ||
                !sessionsQuery.canPrevious
              }
              onClick={sessionsQuery.previous}
            >
              Previous sessions
            </Button>
            <Button
              size="small"
              disabled={
                overrideInFlight.current ||
                !sessionsReady ||
                !sessionsQuery.canNext
              }
              onClick={sessionsQuery.next}
            >
              {sessionsQuery.data?.examined_track_complete
                ? "Next option"
                : "Next sessions"}
            </Button>
          </Stack>
        ) : null}
      </Stack>

      <Menu
        anchorEl={menuAnchor}
        onClose={closeMenu}
        open={sessionsReady && menuAnchor !== null}
      >
        {menuSession &&
        (!menuSession.is_override ||
          (program.status === "published" &&
            menuSession.track_id &&
            trackIsCurrentlyActive(menuSession) &&
            menuSession.override_action === "reschedule" &&
            !isPastSession(menuSession) &&
            Array.isArray(menuSession.court_ids))) ? (
          <MenuItem onClick={() => openReschedule(menuSession)}>
            Reschedule
          </MenuItem>
        ) : null}
        {menuSession && !menuSession.is_override ? (
          <MenuItem onClick={() => openCancel(menuSession)}>
            Cancel session
          </MenuItem>
        ) : null}
        {menuSession &&
        menuSession.override_action !== "cancel" &&
        !isPastSession(menuSession) &&
        courts.length > 0 ? (
          <MenuItem onClick={() => openReassign(menuSession)}>
            Reassign courts
          </MenuItem>
        ) : null}
        {menuSession?.is_override ? (
          <MenuItem onClick={() => openRemove(menuSession)}>
            Remove override
          </MenuItem>
        ) : null}
      </Menu>

      <Dialog
        aria-labelledby="cancel-session-title"
        fullWidth
        maxWidth="sm"
        onClose={closeCancel}
        open={sessionsReady && cancelTarget !== null}
      >
        <Box component="form" onSubmit={confirmCancel}>
          <DialogTitle id="cancel-session-title">
            Cancel this session?
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              {cancelTarget
                ? `The ${formatTimeRange(cancelTarget.start_time, cancelTarget.end_time, timeZone)} session on ${formatDayLabel(cancelTarget.start_time, timeZone)} will be marked cancelled. You can remove the override later to restore it.`
                : ""}
            </DialogContentText>
            <TextField
              fullWidth
              id="session-cancel-reason"
              label="Reason (optional)"
              multiline
              placeholder="Why is this session being cancelled?"
              rows={3}
              {...cancelForm.register("reason")}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeCancel} size="small">
              Keep session
            </Button>
            <Button
              color="error"
              disabled={!sessionsReady}
              size="small"
              type="submit"
              variant="contained"
            >
              {createOverrideMutation.isPending
                ? "Cancelling..."
                : "Cancel session"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        aria-labelledby="reschedule-session-title"
        fullWidth
        maxWidth="sm"
        onClose={closeReschedule}
        open={sessionsReady && rescheduleTarget !== null}
      >
        <Box component="form" onSubmit={confirmReschedule}>
          <DialogTitle id="reschedule-session-title">
            Reschedule this session?
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              {rescheduleTarget
                ? `Pick a new start and end time (shown in ${timeZone}). The original ${formatDayLabel(rescheduleTarget.start_time, timeZone)} occurrence will show as rescheduled.`
                : ""}
            </DialogContentText>
            <Stack spacing={2}>
              <TextField
                error={Boolean(rescheduleForm.formState.errors.newStart)}
                fullWidth
                helperText={rescheduleForm.formState.errors.newStart?.message}
                id="session-new-start"
                label="New start"
                type="datetime-local"
                {...rescheduleForm.register("newStart")}
              />
              <TextField
                error={Boolean(rescheduleForm.formState.errors.newEnd)}
                fullWidth
                helperText={rescheduleForm.formState.errors.newEnd?.message}
                id="session-new-end"
                label="New end"
                type="datetime-local"
                {...rescheduleForm.register("newEnd")}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeReschedule} size="small">
              Keep schedule
            </Button>
            <Button
              disabled={!sessionsReady}
              size="small"
              type="submit"
              variant="contained"
            >
              {createOverrideMutation.isPending
                ? "Rescheduling..."
                : "Reschedule"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        aria-labelledby="remove-override-title"
        fullWidth
        maxWidth="sm"
        onClose={closeRemove}
        open={sessionsReady && removeTarget !== null}
      >
        <DialogTitle id="remove-override-title">
          Remove this override?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {removeTarget
              ? `The ${formatDayLabel(removeTarget.start_time, timeZone)} session will revert to its regular schedule.`
              : ""}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRemove} size="small">
            Keep override
          </Button>
          <Button
            color="error"
            disabled={!sessionsReady}
            onClick={() => void confirmRemove()}
            size="small"
            variant="contained"
          >
            {deleteOverrideMutation.isPending
              ? "Removing..."
              : "Remove override"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        aria-labelledby="reassign-courts-title"
        fullWidth
        maxWidth="sm"
        onClose={closeReassign}
        open={sessionsReady && reassignTarget !== null}
      >
        <Box component="form" onSubmit={confirmReassign}>
          <DialogTitle id="reassign-courts-title">Reassign courts</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              {reassignTarget
                ? `Choose the courts for the ${formatTimeRange(reassignTarget.start_time, reassignTarget.end_time, timeZone)} session on ${formatDayLabel(reassignTarget.start_time, timeZone)}. The time is unchanged — only the courts move.`
                : ""}
            </DialogContentText>
            <FormControl component="fieldset" fullWidth>
              <Typography component="legend" variant="subtitle2">
                Courts
              </Typography>
              <FormGroup>
                {courts.map((court) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={reassignCourtIds.includes(court.id)}
                        onChange={() => {
                          const next = reassignCourtIds.includes(court.id)
                            ? reassignCourtIds.filter((id) => id !== court.id)
                            : [...reassignCourtIds, court.id];
                          reassignForm.setValue("courtIds", next, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                        }}
                      />
                    }
                    key={court.id}
                    label={court.name}
                  />
                ))}
              </FormGroup>
              <FormHelperText>
                Selecting no courts frees this session&apos;s courts entirely.
              </FormHelperText>
            </FormControl>
            {reassignError ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {reassignError}
              </Alert>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeReassign} size="small">
              Cancel
            </Button>
            <Button
              disabled={!sessionsReady}
              size="small"
              type="submit"
              variant="contained"
            >
              {createOverrideMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {attendanceTarget?.track_id ? (
        <AttendanceDialog
          key={`${attendanceTarget.track_id}-${baseOccurrenceIso(attendanceTarget)}`}
          onClose={() => setAttendanceOpen(false)}
          occurrenceStart={baseOccurrenceIso(attendanceTarget)}
          open={attendanceOpen}
          trackId={attendanceTarget.track_id}
          trackName={trackName(attendanceTarget.track_id)}
        />
      ) : null}

      <TeachingEvidenceDialog
        onClose={() => setTeachingTarget(null)}
        open={teachingTarget !== null}
        program={program}
        session={teachingTarget}
      />

      <Portal>
        <Snackbar
          autoHideDuration={5000}
          key={feedback?.message}
          onClose={() => setFeedback(null)}
          open={feedback !== null}
        >
          <Alert
            onClose={() => setFeedback(null)}
            severity={feedback?.severity ?? "success"}
            variant="filled"
          >
            {feedback?.message}
          </Alert>
        </Snackbar>
      </Portal>
    </Paper>
  );
}
